import { useLogoutFunction } from "@propelauth/react";
import { LogOut, ShieldAlert } from "lucide-react";
import React from "react";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/card";
import { getBrandThemeFromDomain, getSupportEmail } from "src/utils/brandTheme";

interface SuspendedOrganizationSplashProps {
  brandTheme?: string | null;
}

export const SuspendedOrganizationSplash: React.FC<SuspendedOrganizationSplashProps> = ({ brandTheme }) => {
  const logout = useLogoutFunction();

  const effectiveTheme = brandTheme ?? getBrandThemeFromDomain() ?? "TIERZERO";
  const isRootlyBranded = effectiveTheme === "ROOTLY" || effectiveTheme === "ROOTLY_COBRAND";
  const brandName = isRootlyBranded ? "Rootly AI SRE" : "TierZero";
  const supportEmail = getSupportEmail(effectiveTheme);

  const handleLogout = async () => {
    localStorage.removeItem("tz_current_org_id");
    await logout(!isRootlyBranded);
    if (isRootlyBranded) {
      window.location.href = "https://ai-sre.rootly.com";
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 flex items-center justify-center">
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader className="items-center text-center">
            <div className="mb-2 rounded-full bg-destructive/10 p-3">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-xl">Organization Access Suspended</CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              Your organization's access to {brandName} has been suspended. To reactivate, please reach out to{" "}
              <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">
                {supportEmail}
              </a>.
            </CardDescription>
          </CardHeader>
          {/* Sign Out is hidden for Rootly-managed orgs: their identity is
              managed upstream by Rootly SSO, and any in-app logout would be
              instantly re-authenticated on the next page load. */}
          {!isRootlyBranded && (
            <CardContent>
              <Button onClick={handleLogout} variant="outline" className="w-full gap-2">
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};
